<template>
  <!-- 2. 今日处置建议 -->
  <section class="report-section">
    <h2 class="section-title">
      今日处置建议
    </h2>
    <div v-if="suggestions.length" class="suggestion-list">
      <div v-for="item in suggestions" :key="item.id" class="suggestion-card"
        :class="`sug-${(item.category || 'NORMAL').toLowerCase()}`">
        <span class="suggestion-icon-shell" :class="getCategoryClass(item.category)">
          <v-icon size="16" aria-hidden="true">{{ getCategoryIcon(item.category) }}</v-icon>
        </span>
        <span class="suggestion-category" :class="getCategoryClass(item.category)">{{ getCategoryLabel(item.category)
        }}</span>
        <span class="title-separator" aria-hidden="true"></span>
        <span class="suggestion-content">
          <span class="suggestion-sentence">
            <b class="alarm-name">{{ item.alarmContent }}</b>仍有 <b>{{ item.unrecoveredCount || 0 }}</b> 条未恢复，{{
              formatSuggestion(item.impact) }}
          </span>
          <span class="suggestion-stations" :title="getAllStationNames(item)">
            涉及
            <template v-if="getStationNames(item)">
              {{ getStationNames(item) }}<template v-if="getStationCount(item) > 2">等</template><template v-else>，共
              </template>
            </template>
            <b class="station-count">{{ getStationCount(item) }}</b> 个电站
          </span>
        </span>
        <v-btn v-if="item.stations?.length" class="suggestion-view-btn" size="small" variant="text" color="primary"
          @click="$emit('view', item)">
          <v-icon size="14" start>mdi-filter-outline</v-icon>跟踪处置
        </v-btn>
      </div>
    </div>
    <div v-else class="empty-inline">今日暂无需重点处置事项</div>
  </section>
</template>

<script setup>
import { getCategoryClass, getCategoryIcon, getCategoryLabel } from './category'

defineProps({
  suggestions: { type: Array, default: () => [] },
})
defineEmits(['view'])

function formatSuggestion(value) {
  const text = String(value || '').trim().replace(/[，。！？；,.!?;]+$/u, '')
  return text || '请尽快处理'
}

function getAllStationNames(item) {
  return (item.stations || []).map(station => station.stationName).filter(Boolean).join('、')
}

function getStationNames(item) {
  return (item.stations || []).slice(0, 2).map(station => station.stationName).filter(Boolean).join('、')
}

function getStationCount(item) {
  return item.stationCount ?? (item.stations || []).length
}
</script>

<style scoped lang="scss">
@import './sectionShell.scss';
@import './category.scss';

.suggestion-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* 单列等权卡片，内容保持为一条连续阅读线。 */
.suggestion-card {
  position: relative;
  display: flex;
  align-items: center;
  min-width: 0;
  min-height: 58px;
  padding: 12px 14px 12px 16px;
  overflow: hidden;
  border-radius: 8px;
  border: 1px solid rgba(31, 42, 68, 0.08);
  background: #fff;
  border-color: rgba(58, 104, 255, 0.14);
  box-shadow: 0 2px 9px rgba(31, 42, 68, 0.02);
  transition: box-shadow 0.2s, border-color 0.2s;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    width: 2px;
    height: 32px;
    border-radius: 0 999px 999px 0;
    background: rgba(58, 104, 255, 0.85);
    transform: translateY(-50%);
  }

  &.sug-safety::before {
    background: rgba(255, 30, 30, 0.85);
  }

  &.sug-income::before {
    background: rgba(245, 158, 11, 0.9);
  }

  &:hover {
    border-color: rgba(58, 104, 255, 0.18);
    box-shadow: 0 5px 18px rgba(31, 42, 68, 0.06);
  }
}

.suggestion-icon-shell {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  transform: translate3d(0, 0, 0);
  backface-visibility: hidden;
  will-change: transform;
  animation: suggestion-icon-float 1.2s ease-in-out infinite;

  &.cat-safety {
    color: rgba(255, 30, 30, 1);
    // background: rgba(255, 30, 30, 0.07);
  }

  &.cat-income {
    color: rgba(245, 158, 11, 1);
    // background: rgba(245, 158, 11, 0.07);
  }

  &.cat-normal {
    color: rgba(58, 104, 255, 1);
    // background: rgba(58, 104, 255, 0.07);
  }
}

@keyframes suggestion-icon-float {

  0%,
  100% {
    transform: translate3d(0, 1px, 0);
  }

  50% {
    transform: translate3d(0, -2px, 0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .suggestion-icon-shell {
    animation: none;
  }
}

.suggestion-category {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  width: auto !important;
  height: auto !important;
  margin-left: 4px;
  background: transparent !important;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  line-height: 18px;

  &.cat-safety {
    color: rgba(255, 30, 30, 1);
  }

  &.cat-income {
    color: rgba(245, 158, 11, 1);
  }

  &.cat-normal {
    color: rgba(58, 104, 255, 1);
  }
}

.title-separator {
  flex-shrink: 0;
  width: 1px;
  height: 18px;
  margin: 0 16px;
  background: rgba(46, 46, 46, 0.24);
}

.suggestion-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.suggestion-sentence,
.suggestion-stations {
  overflow: hidden;
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.suggestion-sentence {
  color: rgba(46, 46, 46, 0.68);

  b {
    color: #2e2e2e;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }

  .alarm-name {
    margin-right: 4px;
    color: #24262b;
  }
}

.suggestion-stations {
  color: rgba(46, 46, 46, 0.5);
  font-size: 12px;
  line-height: 18px;

  .station-count {
    color: #2e2e2e;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
}

.suggestion-view-btn {
  flex-shrink: 0;
  margin-left: 14px;
  min-width: auto;
  height: 26px;
  padding: 0 2px 0 4px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;

  :deep(.v-btn__content) {
    text-transform: none;
    letter-spacing: 0;
  }
}
</style>
